import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { PipelineMasterSteps, SelectedOnboardingAction } from 'global-stores';
import { ActionList } from 'views/complex-onboarding-flow/constant';
import { NodesState } from 'views/complex-onboarding-flow/stores/states';
import { ComplexSettingFormState } from 'views/pipeline';
import { AddBlockButton } from '../add-button';
import { Label } from '../condition-label';
import { RateDrawer } from './rate-drawer';
import { RenderQuestion } from './render-questions';
import { isRowOn } from './row-default';
import { useDataValidationRates } from './use-data-validation-rates';
import {
	KYC_ROW_CHECK,
	findKycRow,
	kycRowPrice,
} from 'views/complex-onboarding-flow/pricing/kyc-pricing';

/*
 * Which catalog row prices each tile row, and at what figure, now lives in
 * `pricing/kyc-pricing.ts` — shared with the node total, the configuration panel
 * and Publish, so the four can no longer drift apart.
 *
 * This file used to carry its own labelId -> key map (`ROW_METADATA_KEYS`). It
 * named only the NEW catalog keys, so on a catalog seeded before the rename it
 * matched nothing — and every row fell back to the `allId` ACTION price, which
 * `/pipeline-init` sets to the Didit data-validation floor. That is how Global
 * Document Validation and Criminal Background Check came to read $0.06 on dev2,
 * over a total that counted them as nothing.
 */

export interface IRenderBlock {
	label?: string | undefined;
	labelId: string;
	stepId: string;
	nodeId: string;
	actionId: string;
}

export const RenderBlock: FC<IRenderBlock> = ({
	label,
	labelId,
	stepId,
	nodeId,
	actionId,
}) => {
	const [nodes, setNodes] = useRecoilState(NodesState);
	const pipelinesSteps = useRecoilValue(PipelineMasterSteps);
	const setSelectedOnboarding = useRecoilValue(SelectedOnboardingAction);

	// Local variables
	const [blockCount, setBlockCount] = useState(0);

	const { conditions: kycActionNodes = [] } = useMemo(
		() => nodes?.find((el: any) => el.id === nodeId) ?? {},
		[nodeId, nodes]
	);

	/*
	@avinashSatschel
    # Set the block count value for "modify" and "clone" operations
    # Since the node already has a block 0 and other blocks, we need to find the
    # maximum block number currently in use and set it as the block count to avoid
    # block number conflicts when modifying or cloning the node.
    # This ensures that the new node has a unique block number.
    */
	useEffect(() => {
		if (/modify|clone/.test(setSelectedOnboarding.type)) {
			const currentNodesConditions = nodes.find(
				(el: any) => el.id === nodeId
			)?.conditions;

			if (currentNodesConditions?.length > 0) {
				const allNumber = [];

				for (let i = 0; i < currentNodesConditions.length; i++) {
					allNumber.push(
						currentNodesConditions[i]?.blockId?.split('_')?.[1] ?? 0
					);
				}

				setBlockCount(Math.max(...allNumber) + 1);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const addNewBlock = useCallback(() => {
		setBlockCount(prev => prev + 1);
		const payload = {
			blockId: `block_${blockCount + 1}`,
			currentId: nodeId,
			and: [],
			labelId,
		};

		setNodes((prev: any) => {
			const prevObj = JSON.parse(JSON.stringify(prev));
			const findIndex = prevObj.findIndex((el: any) => el.id === nodeId);

			if (findIndex !== -1) {
				prevObj[findIndex].conditions.push(payload);
			}
			return prevObj;
		});
	}, [blockCount, nodeId, labelId, setNodes]);

	const allList = useMemo(
		() =>
			kycActionNodes.filter(
				(el: any) => el.currentId === nodeId && el.labelId === labelId
			),
		[kycActionNodes, labelId, nodeId]
	);

	const metaData = useMemo(
		() =>
			pipelinesSteps.find(
				pipelineStep => pipelineStep.key === ActionList[actionId]
			)?.actions[0]?.metadata ?? [],
		[actionId, pipelinesSteps]
	);

	/* The check this row prices (`documentValidation`, `dataValidation`, ...).
	   Also the settings key the checkbox writes. */
	const check = KYC_ROW_CHECK[labelId] ?? labelId;

	/* The BILLED price of this row, or undefined when the catalog does not price
	   it — never another row's price. `undefined` renders no figure, which is the
	   honest reading of "unpriced". SSN stays free by product decision; see
	   kycRowPrice. */
	const price = useMemo(
		() => kycRowPrice(metaData as any, labelId),
		[labelId, metaData]
	);

	/*
	 * __simplici_data_validation_per_country__: Global Data Validation is not a
	 * flat check. Didit charges per registry and per country, so `/pipeline-init`
	 * overlays this row from the vendor rate card and marks it
	 * `priceKind: 'country'` — the figure beside the row is ONE country's
	 * (`priceCountry`), not the price of the check. The chip below is how the
	 * other 44 are read without leaving the builder.
	 *
	 * Driven off `priceKind`, never off the key: a second row priced this way
	 * gets the same affordance for free, and a catalog that goes back to a flat
	 * figure silently drops it rather than promising a sheet that isn't there.
	 */
	const vendorMeta = useMemo(
		() => findKycRow(metaData as any, check) as any,
		[metaData, check]
	);
	const vendorPriced = vendorMeta?.priceKind === 'country';

	const [ratesOpen, setRatesOpen] = useState(false);
	const closeRates = useCallback(() => setRatesOpen(false), []);

	/* Loaded up front rather than on the click: it is one small cached request
	   for the whole canvas, it lets the chip state a real number instead of a
	   vague "see all", and the sheet is then already there when it opens. */
	const { sheet } = useDataValidationRates(vendorPriced);
	const countryCount = sheet?.countries?.length ?? null;

	// __simplici_data_validation_no_conditions__: Global Data Validation is a check
	// that either runs or does not -- there is nothing to branch on -- so it takes
	// no condition blocks.
	//
	// The KYC & AML tile now carries NO condition builder on any row. The design
	// of record draws this step as a plain ledger of purchasable checks -- a
	// checkbox, a name and a price per line, then a total -- and the per-row
	// "Add new condition block" stacks turned it into the tallest, busiest node
	// on the canvas. Widened from `labelId !== 'address'` rather than deleted so
	// the builder underneath (RenderQuestion, addNewBlock, the node's own
	// `conditions` array) is untouched and one line brings it back.
	//
	// NOTE this removes the only way to author a KYC branch condition from the
	// canvas; existing conditions on a saved flow still load, publish and run.
	const showConditions = false;

	// __simplici_kyc_validation_checkboxes__: the purchasable checks. The key is
	// the one kyc.api reads and complex-publish-api serialises, and it is the same
	// key the catalog prices the row under — KYC_ROW_CHECK (pricing/kyc-pricing.ts)
	// is that one map. Rows with no entry there (liveness) are not independently
	// purchasable and get no box.
	//
	// __simplici_kyc_ssn_verification__: SSN rides on the data-validation call, so
	// it is purchasable-but-free and still gets its own box — the checkbox is what
	// the applicant's SSN screen is gated on.
	//
	// '' for any labelId with no checkbox — keeps the `!checkKey` guards below
	// working and the lookups below safely indexable.
	const checkKey = KYC_ROW_CHECK[labelId] ?? '';
	const [complexSettingForm, setComplexSettingForm] = useRecoilState(
		ComplexSettingFormState
	);
	// Unset means on for the free/near-free checks, exactly as before; a PAID
	// opt-in row is off until it is ticked. See row-default.ts.
	const checked = isRowOn(complexSettingForm?.[nodeId]?.[checkKey], vendorMeta);

	const toggleCheck = useCallback(() => {
		if (!checkKey) return;
		setComplexSettingForm((prev: any) => {
			const next = structuredClone(prev);
			if (!next[nodeId]) next[nodeId] = {};
			next[nodeId][checkKey] = next[nodeId][checkKey] === false;
			return next;
		});
	}, [checkKey, nodeId, setComplexSettingForm]);

	// stepVariables can carry a labelId with no entry in `labelData` (the KYC step
	// ships one the catalog never names). <Label> then renders an empty left cell
	// with a price beside it -- on the canvas that reads as a stray, unexplained
	// "$0.75/Verification" line under the row above it. A row with no name is not
	// a row.
	if (!label) return null;

	return (
		<>
			{checkKey && !showConditions ? (
				/* __simplici_kyc_aml_single_tile__: this check has NO condition builder
					under it (see __simplici_data_validation_no_conditions__), so the dark
					<Label> pill is a tab for nothing -- it reads as a heading over the
					rows below rather than as one row. Render it plainly, the same shape
					as Global PEPs & Sanctions. */
				<label
					className={
						labelId === 'ssnVerification'
							? 'kyc-action__plain-row kyc-action__sub-row nodrag'
							: 'kyc-action__plain-row nodrag'
					}
					onClick={e => e.stopPropagation()}
				>
					<input
						type="checkbox"
						className="nodrag"
						checked={checked}
						onChange={toggleCheck}
						aria-label={label}
					/>
					{vendorPriced ? (
						/* A per-country check carries its own second line. The price
							beside it is what the customer is BILLED (the price book);
							the chip is INFORMATION about the vendor's per-country cost
							and opens the sheet. It used to read "US rate", which was
							true while the figure beside it was the US vendor rate — and
							would now falsely label the billed price as one. */
						<span className="kyc-action__label-stack">
							<span className="kyc-action__plain-label">{label}</span>
							<button
								type="button"
								className="kyc-action__rate-chip nodrag"
								aria-haspopup="dialog"
								aria-expanded={ratesOpen}
								title="Vendor cost varies by country — open the rate sheet. You are billed the price shown."
								onClick={e => {
									/* preventDefault: this button is inside the row's <label>,
										so a click would otherwise toggle the checkbox too. */
									e.preventDefault();
									e.stopPropagation();
									setRatesOpen(prev => !prev);
								}}
							>
								<span className="kyc-action__rate-chip-text">
									Vendor rates ·{' '}
									{countryCount ? `${countryCount} countries` : 'all countries'}
								</span>
								<span className="kyc-action__rate-chip-arrow">›</span>
							</button>
						</span>
					) : (
						<span className="kyc-action__plain-label">{label}</span>
					)}
					{!!price && (
						<span className="kyc-action__plain-price">
							${Number(price).toFixed(2)}
							<span className="kyc-action__plain-unit">/Verification</span>
						</span>
					)}
					{vendorPriced && (
						<RateDrawer
							open={ratesOpen}
							onClose={closeRates}
							country={vendorMeta?.priceCountry}
							priceNote={vendorMeta?.priceNote}
						/>
					)}
				</label>
			) : checkKey ? (
				<div className="kyc-action__check-row">
					{/* `nodrag` + stopPropagation: this tile is a react-flow node, and
						without them clicking the box starts a node drag instead. */}
					<input
						type="checkbox"
						className="nodrag"
						checked={checked}
						onChange={toggleCheck}
						onClick={e => e.stopPropagation()}
						aria-label={label}
					/>
					<Label label={label} price={price} priceLabel="Verification" />
				</div>
			) : (
				<Label label={label} price={price} priceLabel="Verification" />
			)}
			{showConditions && (
				<div
					className="kyc-action__questions-wrapper"
					style={{ position: 'relative' }}
				>
					{allList?.map((item: any) => (
						<RenderQuestion
							labelId={labelId}
							stepId={stepId}
							nodeId={nodeId}
							key={`${item.blockId}_${item.labelId}`}
							blockId={item.blockId}
						/>
					))}

					<AddBlockButton onClick={addNewBlock} />
				</div>
			)}
		</>
	);
};
